import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaLock, FaCheckCircle, FaCheckDouble, FaUnlockAlt,
    FaUserShield, FaChevronLeft, FaChevronRight, FaArrowRight,
    FaExclamationCircle, FaShieldAlt, FaSearch, FaCalendarAlt,
    FaFilePdf, FaRocket, FaTimesCircle, FaSearchDollar,
    FaChartPie, FaSpinner, FaUniversity, FaFileInvoiceDollar,
    FaChevronDown, FaChevronUp, FaListAlt, FaBolt,
} from 'react-icons/fa';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import './FechamentoFinanceiro.css';

/* ─── HELPERS ────────────────────────────────────────────────── */
const fmtCurrency = (v) =>
    parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (s) => {
    if (!s) return '—';
    const d = s.includes('T') ? s : `${s}T00:00:00`;
    return new Date(d).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
    });
};

/* ─── CONSTANTES ─────────────────────────────────────────────── */
const MONTHS_SHORT = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const MONTHS_FULL  = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

/* ─── SCORE RING ─────────────────────────────────────────────── */
const ScoreRing = ({ score }) => {
    const r      = 52;
    const circ   = 2 * Math.PI * r;
    const offset = circ - (score / 100) * circ;
    const color  = score === 100 ? '#00e5a0' : score >= 60 ? '#f59e0b' : '#ff4d6d';
    const label  = score === 100 ? 'PRONTO'  : score >= 60 ? 'PARCIAL' : 'BLOQUEADO';

    return (
        <div className="score-ring">
            <svg width="130" height="130" viewBox="0 0 130 130">
                <defs>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
                <circle cx="65" cy="65" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="10" fill="none" />
                <circle
                    cx="65" cy="65" r={r}
                    stroke={color} strokeWidth="10" fill="none"
                    strokeDasharray={circ} strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform="rotate(-90 65 65)"
                    filter="url(#glow)"
                    style={{ transition: 'stroke-dashoffset 1.6s cubic-bezier(0.4,0,0.2,1)' }}
                />
            </svg>
            <div className="score-ring-center">
                <span className="score-pct" style={{ color }}>{score}%</span>
                <span className="score-lbl" style={{ color }}>{label}</span>
            </div>
        </div>
    );
};

/* ─── MONTH PICKER ───────────────────────────────────────────── */
const MonthPicker = ({ value, onChange }) => {
    const [open,     setOpen]     = useState(false);
    const [pickYear, setPickYear] = useState(
        () => parseInt(value?.split('-')[0]) || new Date().getFullYear()
    );
    const ref = useRef();

    const selYear  = parseInt(value?.split('-')[0]);
    const selMonth = parseInt(value?.split('-')[1]) - 1;
    const now      = new Date();

    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const pick = (i) => {
        onChange(`${pickYear}-${String(i + 1).padStart(2, '0')}`);
        setOpen(false);
    };

    return (
        <div className="mp-root" ref={ref}>
            <button className="mp-trigger" onClick={() => setOpen(o => !o)}>
                <FaCalendarAlt className="mp-cal-icon" />
                <div className="mp-label">
                    <span className="mp-full-month">{MONTHS_FULL[selMonth]}</span>
                    <span className="mp-year-label">{selYear}</span>
                </div>
                <FaChevronRight className={`mp-caret ${open ? 'open' : ''}`} />
            </button>
            {open && (
                <div className="mp-dropdown">
                    <div className="mp-year-row">
                        <button onClick={() => setPickYear(y => y - 1)}><FaChevronLeft /></button>
                        <span className="mp-pick-year">{pickYear}</span>
                        <button onClick={() => setPickYear(y => y + 1)}><FaChevronRight /></button>
                    </div>
                    <div className="mp-grid">
                        {MONTHS_SHORT.map((m, i) => {
                            const isSel    = pickYear === selYear && i === selMonth;
                            const isCurr   = pickYear === now.getFullYear() && i === now.getMonth();
                            const isFuture = pickYear > now.getFullYear() ||
                                             (pickYear === now.getFullYear() && i > now.getMonth());
                            return (
                                <button
                                    key={i}
                                    className={`mp-cell ${isSel ? 'sel' : ''} ${isCurr ? 'curr' : ''} ${isFuture ? 'fut' : ''}`}
                                    onClick={() => !isFuture && pick(i)}
                                    disabled={isFuture}
                                >{m}</button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ─── CLOSING CEREMONY ───────────────────────────────────────── */
const ClosingCeremony = ({ periodo, onDone }) => {
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        const t1 = setTimeout(() => setPhase(1), 400);
        const t2 = setTimeout(() => setPhase(2), 1400);
        const t3 = setTimeout(() => setPhase(3), 2600);
        const t4 = setTimeout(() => onDone(),    4000);
        return () => [t1,t2,t3,t4].forEach(clearTimeout);
    }, []);

    const particles = useMemo(() => Array.from({ length: 30 }, (_, i) => ({
        id:       i,
        x:        Math.random() * 100,
        delay:    Math.random() * 1.5,
        duration: 2 + Math.random() * 2,
        size:     4 + Math.random() * 8,
        color:    ['#00e5a0','#3b82f6','#f59e0b','#a78bfa','#ff6b6b'][i % 5],
    })), []);

    return (
        <div className={`cc-overlay phase-${phase}`}>
            {particles.map(p => (
                <div key={p.id} className="cc-particle" style={{
                    left:              `${p.x}%`,
                    width:             p.size,
                    height:            p.size,
                    background:        p.color,
                    animationDelay:    `${p.delay}s`,
                    animationDuration: `${p.duration}s`,
                }} />
            ))}
            <div className="cc-box">
                <div className={`cc-lock-wrap ${phase >= 1 ? 'active' : ''}`}>
                    <FaLock className="cc-lock-icon" />
                    {phase >= 1 && <div className="cc-pulse-ring" />}
                    {phase >= 1 && <div className="cc-pulse-ring delay" />}
                </div>
                {phase >= 1 && <h1 className="cc-heading">Mês Consolidado!</h1>}
                {phase >= 2 && <p className="cc-sub">{periodo} foi lacrado e auditado com sucesso</p>}
                {phase >= 3 && (
                    <div className="cc-redirect">
                        <div className="cc-redirect-bar" />
                        <span>Abrindo Conciliação Bancária...</span>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ─── AUDIT ITEM (com suporte a blocker/warn) ────────────────── */
const AuditItem = ({ label, sub, count, Icon, onAction, actionLabel, index, blocker = true }) => {
    const ok        = count === 0;
    const stateClass = ok ? 'ok' : blocker ? 'err' : 'warn';

    return (
        <div className={`ai-item ${stateClass}`} style={{ animationDelay: `${index * 0.08}s` }}>
            <div className={`ai-dot ${stateClass}`}>
                {ok ? <FaCheckCircle /> : <Icon />}
            </div>
            <div className="ai-body">
                <div className="ai-row">
                    <strong>{label}</strong>
                    <span className={`ai-badge ${stateClass}`}>
                        {ok
                            ? '✓ Aprovado'
                            : blocker
                                ? `${count} bloqueante${count !== 1 ? 's' : ''}`
                                : `${count} informativo${count !== 1 ? 's' : ''}`}
                    </span>
                </div>
                <p>{sub}</p>
                {!ok && (
                    <button className={`ai-cta ${!blocker ? 'warn' : ''}`} onClick={onAction}>
                        {actionLabel} <FaArrowRight />
                    </button>
                )}
            </div>
        </div>
    );
};

/* ─── ROTINAS VIEW ───────────────────────────────────────────── */
const RotinasView = ({ dados, navigate }) => {
    const [expanded, setExpanded] = useState(null);

    const categories = useMemo(() => [
        {
            id:      'conciliados',
            icon:    <FaCheckCircle />,
            label:   'Conciliados via Extrato',
            sub:     'Lançamentos com correspondência confirmada no extrato bancário (OFX)',
            items:   dados.conciliados_extrato || [],
            color:   '#00e5a0',
            blocker: false,
            status:  'ok',
            emptyMsg: 'Nenhum lançamento OFX processado neste período.',
        },
        {
            id:      'internos',
            icon:    <FaShieldAlt />,
            label:   'Confirmados pelo Sistema',
            sub:     'Pagamentos apontados manualmente — sistema confirmou, extrato bancário ainda não verificado',
            items:   dados.confirmados_internamente || [],
            color:   '#3b82f6',
            blocker: false,
            status:  'info',
            action:  { label: 'Importar Extrato OFX', route: '/financeiro/conciliacao-bancaria' },
            emptyMsg: 'Todos os pagamentos já foram verificados via extrato.',
        },
        {
            id:      'floating',
            icon:    <FaExclamationCircle />,
            label:   'Lançamentos sem Origem',
            sub:     'Entradas/saídas no caixa sem correspondência com nenhuma conta cadastrada — precisam ser identificadas',
            items:   dados.pendencias_conciliacao || [],
            color:   '#ff4d6d',
            blocker: true,
            status:  'error',
            action:  { label: 'Ir para Conciliação Bancária', route: '/financeiro/conciliacao-bancaria' },
            emptyMsg: 'Nenhum lançamento flutuante. Caixa 100% identificado.',
        },
        {
            id:      'pendentes',
            icon:    <FaFileInvoiceDollar />,
            label:   'Títulos em Aberto',
            sub:     'Contas a pagar/receber ainda não liquidadas neste período',
            items:   dados.pendencias_aprovacao || [],
            color:   '#f59e0b',
            blocker: true,
            status:  'warn',
            action:  { label: 'Ver Contas a Pagar', route: '/financeiro/contas-a-pagar' },
            emptyMsg: 'Nenhum título pendente. Período completamente liquidado.',
        },
    ], [dados]);

    const totalBlockers  = categories.filter(c => c.blocker).reduce((a, c) => a + c.items.length, 0);
    const totalConcil    = (dados.conciliados_extrato     || []).length;
    const totalConfirm   = (dados.confirmados_internamente|| []).length;

    const toggleExpand = (id) => setExpanded(e => e === id ? null : id);

    return (
        <div className="rotinas-root">

            {/* ── SUMMARY CHIPS ──────────────────────────────── */}
            <div className="rot-summary">
                {categories.map(cat => {
                    const total = cat.items.reduce((a, i) => a + Math.abs(parseFloat(i.valor || 0)), 0);
                    return (
                        <button
                            key={cat.id}
                            className={`rot-chip status-${cat.status} ${expanded === cat.id ? 'active' : ''}`}
                            onClick={() => toggleExpand(cat.id)}
                        >
                            <span className="rot-chip-icon" style={{ color: cat.color }}>{cat.icon}</span>
                            <div className="rot-chip-body">
                                <strong style={{ color: cat.color }}>{cat.items.length}</strong>
                                <span>{cat.label}</span>
                                <em>{fmtCurrency(total)}</em>
                            </div>
                            <div className="rot-chip-meta">
                                {cat.blocker && cat.items.length > 0 && (
                                    <span className="rot-tag blocker">BLOQUEANTE</span>
                                )}
                                {!cat.blocker && (
                                    <span className="rot-tag info">INFORMATIVO</span>
                                )}
                                <span className="rot-chip-caret">
                                    {expanded === cat.id ? <FaChevronUp /> : <FaChevronDown />}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* ── STATUS BANNER ──────────────────────────────── */}
            {totalBlockers > 0 ? (
                <div className="rot-banner error">
                    <FaExclamationCircle className="rot-banner-icon" />
                    <div>
                        <strong>{totalBlockers} item(ns) bloqueando o fechamento</strong>
                        <span>
                            Resolva os lançamentos sem origem e os títulos em aberto
                            antes de consolidar o período.
                        </span>
                    </div>
                </div>
            ) : (
                <div className="rot-banner ok">
                    <FaCheckDouble className="rot-banner-icon" />
                    <div>
                        <strong>Período pronto para fechamento!</strong>
                        <span>
                            {totalConcil} conciliado(s) via extrato bancário ·{' '}
                            {totalConfirm} confirmado(s) internamente · Nenhum bloqueante.
                        </span>
                    </div>
                </div>
            )}

            {/* ── EXPANDABLE PANELS ──────────────────────────── */}
            <div className="rot-panels">
                {categories.map(cat => (
                    <div key={cat.id} className={`rot-panel status-${cat.status} ${expanded === cat.id ? 'open' : ''}`}>

                        {/* Panel header */}
                        <div className="rot-panel-head" onClick={() => toggleExpand(cat.id)}>
                            <div className="rot-ph-left">
                                <span className="rot-ph-icon" style={{ color: cat.color }}>{cat.icon}</span>
                                <div className="rot-ph-text">
                                    <h4>{cat.label}</h4>
                                    <p>{cat.sub}</p>
                                </div>
                            </div>
                            <div className="rot-ph-right">
                                {cat.blocker && cat.items.length > 0 && (
                                    <span className="rot-tag blocker">⛔ BLOQUEANTE</span>
                                )}
                                {!cat.blocker && cat.items.length > 0 && (
                                    <span className="rot-tag info">ℹ️ INFORMATIVO</span>
                                )}
                                {cat.items.length === 0 && (
                                    <span className="rot-tag ok">✅ OK</span>
                                )}
                                <strong className="rot-ph-count" style={{ color: cat.color }}>
                                    {cat.items.length}
                                </strong>
                                <span className="rot-ph-caret">
                                    {expanded === cat.id ? <FaChevronUp /> : <FaChevronDown />}
                                </span>
                            </div>
                        </div>

                        {/* Panel body */}
                        {expanded === cat.id && (
                            <div className="rot-panel-body">
                                {cat.items.length === 0 ? (
                                    <div className="rot-empty">
                                        <FaCheckCircle style={{ color: '#00e5a0', fontSize: '1.4rem' }} />
                                        <span>{cat.emptyMsg}</span>
                                    </div>
                                ) : (
                                    <>
                                        {/* Informativo especial para "Confirmados" */}
                                        {cat.id === 'internos' && (
                                            <div className="rot-info-bar">
                                                <FaShieldAlt />
                                                <span>
                                                    Estes pagamentos foram confirmados pelo sistema quando você
                                                    apontou o recebimento/pagamento. O extrato OFX é uma
                                                    verificação adicional — <strong>não bloqueia o fechamento.</strong>
                                                </span>
                                            </div>
                                        )}

                                        {/* Informativo especial para "Floating" */}
                                        {cat.id === 'floating' && (
                                            <div className="rot-info-bar danger">
                                                <FaExclamationCircle />
                                                <span>
                                                    Estes lançamentos estão no caixa mas não possuem
                                                    correspondência com nenhuma conta a pagar ou receber.
                                                    Identifique cada um na Conciliação Bancária.
                                                </span>
                                            </div>
                                        )}

                                        {/* Tabela de itens */}
                                        <div className="rot-table">
                                            <div className="rot-table-head">
                                                <span>Data</span>
                                                <span>Descrição</span>
                                                <span>Valor</span>
                                                <span>Status</span>
                                            </div>
                                            <div className="rot-table-body">
                                                {cat.items.slice(0, 25).map((item, i) => (
                                                    <div key={item.id || i} className="rot-row">
                                                        <span className="rot-row-date">
                                                            {fmtDate(item.data || item.data_vencimento)}
                                                        </span>
                                                        <span className="rot-row-desc" title={item.descricao}>
                                                            {item.descricao}
                                                        </span>
                                                        <span className={`rot-row-val ${parseFloat(item.valor || 0) >= 0 ? 'pos' : 'neg'}`}>
                                                            {fmtCurrency(item.valor)}
                                                        </span>
                                                        <span className="rot-row-action">
                                                            {cat.id === 'floating' && (
                                                                <button
                                                                    className="rot-btn danger"
                                                                    onClick={() => navigate('/financeiro/conciliacao-bancaria')}
                                                                >
                                                                    Identificar
                                                                </button>
                                                            )}
                                                            {cat.id === 'pendentes' && (
                                                                <button
                                                                    className="rot-btn warn"
                                                                    onClick={() => navigate('/financeiro/contas-a-pagar')}
                                                                >
                                                                    Liquidar
                                                                </button>
                                                            )}
                                                            {cat.id === 'internos' && (
                                                                <span className="rot-status-badge info">
                                                                    Sistema ✓
                                                                </span>
                                                            )}
                                                            {cat.id === 'conciliados' && (
                                                                <span className="rot-status-badge ok">
                                                                    OFX ✓
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>

                                            {cat.items.length > 25 && (
                                                <div className="rot-table-more">
                                                    +{cat.items.length - 25} itens não exibidos.
                                                    Use a tela específica para ver todos.
                                                </div>
                                            )}
                                        </div>

                                        {/* CTA do painel */}
                                        {cat.action && (
                                            <div className="rot-panel-cta">
                                                <button
                                                    className={`btn-rot-cta ${cat.status}`}
                                                    onClick={() => navigate(cat.action.route)}
                                                >
                                                    <FaArrowRight /> {cat.action.label}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* ── LEGENDA ────────────────────────────────────── */}
            <div className="rot-legend">
                <h5>Como funciona o fluxo de conciliação</h5>
                <div className="rot-leg-items">
                    <div className="rot-leg-item">
                        <span className="rot-leg-dot" style={{ background: '#ff4d6d' }} />
                        <div>
                            <strong>Lançamentos sem Origem</strong> — BLOQUEANTE.
                            São entradas/saídas no livro caixa que não correspondem a
                            nenhuma conta a pagar ou receber. Precisam ser identificadas
                            via Conciliação Bancária.
                        </div>
                    </div>
                    <div className="rot-leg-item">
                        <span className="rot-leg-dot" style={{ background: '#f59e0b' }} />
                        <div>
                            <strong>Títulos em Aberto</strong> — BLOQUEANTE.
                            Contas a pagar ou receber do período que ainda não foram
                            liquidadas. Pague/receba antes de fechar.
                        </div>
                    </div>
                    <div className="rot-leg-item">
                        <span className="rot-leg-dot" style={{ background: '#3b82f6' }} />
                        <div>
                            <strong>Confirmados pelo Sistema</strong> — INFORMATIVO.
                            Pagamentos que você apontou no sistema. O fechamento aceita
                            estes como válidos. Importar o extrato OFX é recomendado
                            para verificação dupla, mas não é obrigatório.
                        </div>
                    </div>
                    <div className="rot-leg-item">
                        <span className="rot-leg-dot" style={{ background: '#00e5a0' }} />
                        <div>
                            <strong>Conciliados via Extrato</strong> — VERIFICADO.
                            Lançamentos com correspondência no extrato bancário (OFX).
                            Nível máximo de verificação contábil.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
export default function FechamentoFinanceiro() {
    const navigate                        = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const periodoURL                      = searchParams.get('periodo') || new Date().toISOString().slice(0, 7);

    const [loading,       setLoading]       = useState(true);
    const [closing,       setClosing]       = useState(false);
    const [ceremony,      setCeremony]      = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [activeTab,     setActiveTab]     = useState('fechamento'); // 'fechamento' | 'rotinas'

    const printRef = useRef(null);

    const user = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    }, []);

    const [dados, setDados] = useState({
        receitas:                 0,
        despesas:                 0,
        saldo:                    0,
        // BLOQUEANTES
        pendencias_conciliacao:   [],   // floating entries (sem bill vinculada)
        pendencias_aprovacao:     [],   // contas ainda não pagas
        // INFORMATIVOS
        confirmados_internamente: [],   // pagos pelo sistema, sem OFX
        conciliados_extrato:      [],   // matchados via OFX
        status_mes:               'aberto',
    });

    useEffect(() => { if (periodoURL?.length === 7) load(); }, [periodoURL]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/financeiro/fechamento', {
                params: { periodo: periodoURL },
            });
            setDados(data);
        } catch {
            toast.error('Erro ao carregar dados de fechamento.');
        } finally {
            setLoading(false);
        }
    };

    const navMes = (d) => {
        const [y, m] = periodoURL.split('-').map(Number);
        const dt     = new Date(y, m - 1 + d, 1);
        setSearchParams({ periodo: dt.toISOString().slice(0, 7) });
    };

    /* ──────────────────────────────────────────────────────────
       SCORE — baseado APENAS nos bloqueantes reais:
       • floating entries (lançamentos sem origem)
       • títulos em aberto
       Confirmados internamente NÃO penalizam o score.
    ────────────────────────────────────────────────────────── */
    const score = useMemo(() => {
        const blockers =
            (dados.pendencias_conciliacao?.length || 0) +
            (dados.pendencias_aprovacao?.length   || 0);
        return blockers === 0 ? 100 : Math.max(5, 100 - blockers * 12);
    }, [dados]);

    const [ano, mesIdx] = [periodoURL.split('-')[0], parseInt(periodoURL.split('-')[1]) - 1];
    const nomeMes       = MONTHS_FULL[mesIdx];

    /* ──────────────────────────────────────────────────────────
       AUDIT ITEMS — distinção clara: blocker vs informativo
    ────────────────────────────────────────────────────────── */
    const auditItems = [
        {
            label:       'Lançamentos sem Origem',
            sub:         'Entradas no caixa sem correspondência com nenhuma conta — precisam ser identificadas',
            count:       dados.pendencias_conciliacao?.length || 0,
            Icon:        FaExclamationCircle,
            actionLabel: 'Conciliar Agora',
            action:      '/financeiro/conciliacao-bancaria',
            blocker:     true,
        },
        {
            label:       'Títulos em Aberto',
            sub:         'Contas a pagar ainda não liquidadas neste período',
            count:       dados.pendencias_aprovacao?.length || 0,
            Icon:        FaFileInvoiceDollar,
            actionLabel: 'Ver Títulos',
            action:      '/financeiro/contas-a-pagar',
            blocker:     true,
        },
        {
            label:       'Aguardando Verificação Bancária',
            sub:         'Pagamentos confirmados pelo sistema — importar OFX é recomendado, mas não obrigatório para fechar',
            count:       dados.confirmados_internamente?.length || 0,
            Icon:        FaUniversity,
            actionLabel: 'Importar Extrato OFX',
            action:      '/financeiro/conciliacao-bancaria',
            blocker:     false,  // ← NÃO bloqueia o fechamento
        },
    ];

    const handleFechar = async () => {
        if (score < 100) {
            toast.error('Resolva os itens bloqueantes antes de consolidar.');
            return;
        }
        if (!window.confirm(
            `Consolidar e fechar ${nomeMes} ${ano}?\nEsta ação impedirá edições retroativas.`
        )) return;
        setClosing(true);
        try {
            await api.post('/api/financeiro/fechar-mes', { periodo: periodoURL });
            setCeremony(true);
        } catch (e) {
            toast.error(e.response?.data?.error || 'Erro ao fechar o mês.');
            setClosing(false);
        }
    };

    const handleReabrir = async () => {
        const isAdmin = user?.perfil === 'admin_geral';
        const msg     = isAdmin
            ? `Reabrir ${periodoURL} imediatamente?`
            : `Solicitar reabertura de ${periodoURL} ao Admin Geral?`;
        if (!window.confirm(msg)) return;
        try {
            await api.post('/api/financeiro/reabrir-mes', {
                periodo: periodoURL,
                perfil:  user.perfil,
            });
            if (isAdmin) { toast.success('Período reaberto!'); load(); }
            else           toast.info('Solicitação enviada.');
        } catch (e) { toast.error(e.response?.data?.error || 'Erro.'); }
    };

    /* ─── PDF EXPORT ─────────────────────────────────────────── */
    const handleExportPdf = async () => {
        if (!printRef.current || generatingPdf) return;
        try {
            const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
                import('html2canvas'),
                import('jspdf'),
            ]);
            await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
            const canvas = await html2canvas(printRef.current, {
                scale:           2,
                useCORS:         true,
                allowTaint:      true,
                backgroundColor: '#0f172a',
                scrollX:         0,
                scrollY:         0,
                windowWidth:     printRef.current.scrollWidth,
                windowHeight:    printRef.current.scrollHeight,
                logging:         false,
                imageTimeout:    0,
            });
            setGeneratingPdf(true);
            await new Promise(r => setTimeout(r, 60));
            const imgData   = canvas.toDataURL('image/png');
            const ratio     = canvas.width / canvas.height;
            const landscape = ratio >= 1;
            const pageW     = landscape ? 297 : 210;
            const pageH     = landscape ? 210 : 297;
            const pdf = new jsPDF({
                orientation: landscape ? 'landscape' : 'portrait',
                unit:        'mm',
                format:      'a4',
            });
            const scaledH = pageW / ratio;
            if (scaledH <= pageH) {
                pdf.addImage(imgData, 'PNG', 0, 0, pageW, scaledH);
            } else {
                let yOffset = 0;
                while (yOffset < scaledH) {
                    if (yOffset > 0) pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, -yOffset, pageW, scaledH);
                    yOffset += pageH;
                }
            }
            const filename = `Fechamento_${periodoURL}_${nomeMes}_${ano}.pdf`;
            pdf.save(filename);
            toast.success(`✔ PDF exportado: ${filename}`);
        } catch (e) {
            console.error('[PDF Export]', e);
            toast.error('Erro ao gerar PDF.');
        } finally {
            setGeneratingPdf(false);
        }
    };

    /* ─── DADOS DERIVADOS ────────────────────────────────────── */
    const pieData = [
        { name: 'Receitas', value: parseFloat(dados.receitas || 0),           color: '#00e5a0' },
        { name: 'Despesas', value: Math.abs(parseFloat(dados.despesas || 0)), color: '#ff4d6d' },
    ];

    const blockerCount = (dados.pendencias_conciliacao?.length || 0) +
                         (dados.pendencias_aprovacao?.length   || 0);

    if (ceremony) {
        return (
            <ClosingCeremony
                periodo={`${nomeMes} ${ano}`}
                onDone={() => navigate('/financeiro/conciliacao-bancaria')}
            />
        );
    }

    /* ═══════════════════════════════════════════════════════════
       RENDER
    ═══════════════════════════════════════════════════════════ */
    return (
        <>
            {/* Overlay de geração de PDF — fora do printRef */}
            {generatingPdf && (
                <div className="pdf-overlay">
                    <div className="pdf-overlay-box">
                        <FaSpinner className="pdf-spinner" />
                        <span>Gerando PDF...</span>
                        <p>Montando o arquivo, aguarde.</p>
                    </div>
                </div>
            )}

            <div className="ff-root" ref={printRef}>

                {/* ── HERO HEADER ──────────────────────────────── */}
                <header className="ff-header">
                    <div className="ff-header-glow" />
                    <div className="ff-header-inner">

                        <div className="ff-header-left">
                            <div className={`ff-status-pill ${dados.status_mes === 'fechado' ? 'closed' : 'open'}`}>
                                {dados.status_mes === 'fechado'
                                    ? <><FaLock /> Período Fechado</>
                                    : <><FaRocket /> Auditoria Ativa</>}
                            </div>
                            <h1 className="ff-title">Fechamento <em>{nomeMes}</em></h1>
                            <p className="ff-subtitle">Consolidação Contábil — Exercício {ano}</p>
                        </div>

                        {/* ── TABS ─────────────────────────────── */}
                        <div className="ff-tabs">
                            <button
                                className={`ff-tab-btn ${activeTab === 'fechamento' ? 'active' : ''}`}
                                onClick={() => setActiveTab('fechamento')}
                            >
                                <FaChartPie /> Fechamento
                            </button>
                            <button
                                className={`ff-tab-btn ${activeTab === 'rotinas' ? 'active' : ''}`}
                                onClick={() => setActiveTab('rotinas')}
                            >
                                <FaListAlt /> Rotinas
                                {blockerCount > 0 && (
                                    <span className="ff-tab-badge">{blockerCount}</span>
                                )}
                            </button>
                        </div>

                        <div className="ff-header-right">
                            <div className="ff-nav-row">
                                <button className="ff-nav-btn" onClick={() => navMes(-1)} title="Mês anterior">
                                    <FaChevronLeft />
                                </button>
                                <MonthPicker
                                    value={periodoURL}
                                    onChange={v => setSearchParams({ periodo: v })}
                                />
                                <button className="ff-nav-btn" onClick={() => navMes(1)} title="Próximo mês">
                                    <FaChevronRight />
                                </button>
                            </div>
                            <button
                                className={`ff-print-btn ${generatingPdf ? 'loading' : ''}`}
                                onClick={handleExportPdf}
                                disabled={generatingPdf || loading}
                                title="Exportar como PDF"
                            >
                                {generatingPdf
                                    ? <><FaSpinner className="btn-spin-icon" /> Gerando...</>
                                    : <><FaFilePdf /> PDF</>}
                            </button>
                        </div>
                    </div>
                </header>

                {/* ── LOADING ──────────────────────────────────── */}
                {loading ? (
                    <div className="ff-loading">
                        <div className="ff-spinner" />
                        <span>Verificando integridade do período...</span>
                    </div>

                /* ── ABA ROTINAS ─────────────────────────────── */
                ) : activeTab === 'rotinas' ? (
    <main className="ff-main">
        <RotinasView dados={dados} navigate={navigate} />
    </main>

                /* ── ABA FECHAMENTO ──────────────────────────── */
                ) : (
                    <main className="ff-main">

                        {/* KPI STRIP */}
                        <div className="ff-kpi-row">
                            <div className="ff-kpi kpi-in">
                                <div className="kpi-line" />
                                <span className="kpi-label">Entradas</span>
                                <strong className="kpi-value">{fmtCurrency(dados.receitas)}</strong>
                            </div>
                            <div className="ff-kpi kpi-out">
                                <div className="kpi-line" />
                                <span className="kpi-label">Saídas</span>
                                <strong className="kpi-value">{fmtCurrency(Math.abs(dados.despesas))}</strong>
                            </div>
                            <div className={`ff-kpi kpi-result ${dados.saldo >= 0 ? 'pos' : 'neg'}`}>
                                <div className="kpi-line" />
                                <span className="kpi-label">Resultado Líquido</span>
                                <strong className="kpi-value">{fmtCurrency(dados.saldo)}</strong>
                            </div>
                            <div className="ff-kpi kpi-score">
                                <ScoreRing score={score} />
                                <span className="kpi-label" style={{ textAlign: 'center', marginTop: 4 }}>
                                    Score de Conformidade
                                </span>
                            </div>
                        </div>

                        {/* CONTENT GRID */}
                        <div className="ff-grid">

                            {/* LEFT: CHART */}
                            <div className="ff-card chart-card">
                                <h3 className="card-title"><FaChartPie /> Composição do Período</h3>
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%" cy="50%"
                                            innerRadius={58} outerRadius={82}
                                            paddingAngle={3}
                                            dataKey="value"
                                        >
                                            {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                        </Pie>
                                        <Tooltip
                                            formatter={v => fmtCurrency(v)}
                                            contentStyle={{
                                                background: '#111827',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: 8, color: '#fff',
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="chart-legend">
                                    {pieData.map(d => (
                                        <div key={d.name} className="legend-item">
                                            <span className="legend-dot" style={{ background: d.color }} />
                                            <span>{d.name}</span>
                                            <strong>{fmtCurrency(d.value)}</strong>
                                        </div>
                                    ))}
                                </div>
                                <div className="balance-track">
                                    <div
                                        className="balance-fill"
                                        style={{
                                            width: pieData[0].value + pieData[1].value > 0
                                                ? `${(pieData[0].value / (pieData[0].value + pieData[1].value)) * 100}%`
                                                : '50%',
                                        }}
                                    />
                                </div>

                                {/* Resumo de conciliação no chart card */}
                                <div className="concil-summary-strip">
                                    <div className="css-item">
                                        <span style={{ color: '#00e5a0' }}>●</span>
                                        <span>{dados.conciliados_extrato?.length || 0} via OFX</span>
                                    </div>
                                    <div className="css-item">
                                        <span style={{ color: '#3b82f6' }}>●</span>
                                        <span>{dados.confirmados_internamente?.length || 0} confirmados</span>
                                    </div>
                                    <div className="css-item">
                                        <span style={{ color: '#ff4d6d' }}>●</span>
                                        <span>{dados.pendencias_conciliacao?.length || 0} sem origem</span>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT: AUDIT */}
                            <div className="ff-card audit-card">
                                {dados.status_mes === 'fechado' ? (
                                    <div className="closed-state">
                                        <div className="closed-icon-wrap">
                                            <FaCheckDouble className="closed-icon" />
                                        </div>
                                        <h2>Período Consolidado</h2>
                                        <p>
                                            Este mês foi auditado, lacrado e os saldos foram
                                            transportados para o período seguinte.
                                        </p>
                                        <button
                                            className="btn-goto-concil"
                                            onClick={() => navigate('/financeiro/conciliacao-bancaria')}
                                        >
                                            <FaSearch /> Ir para Conciliação Bancária <FaArrowRight />
                                        </button>
                                        {(user?.perfil === 'admin' || user?.perfil === 'admin_geral') && (
                                            <div className="reopen-zone">
                                                <button className="btn-reopen" onClick={handleReabrir}>
                                                    {user.perfil === 'admin_geral'
                                                        ? <><FaUnlockAlt /> Reabrir Imediatamente</>
                                                        : <><FaUserShield /> Solicitar Reabertura</>}
                                                </button>
                                                <p className="reopen-hint">
                                                    {user.perfil === 'admin_geral'
                                                        ? 'Desbloqueio direto — Admin Geral.'
                                                        : 'Requer aprovação do Admin Geral.'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <h3 className="card-title">
                                            <FaCheckDouble /> Checklist de Conformidade
                                        </h3>

                                        <div className="audit-list">
                                            {auditItems.map((item, i) => (
                                                <AuditItem
                                                    key={item.label}
                                                    index={i}
                                                    label={item.label}
                                                    sub={item.sub}
                                                    count={item.count}
                                                    Icon={item.Icon}
                                                    actionLabel={item.actionLabel}
                                                    onAction={() => navigate(item.action)}
                                                    blocker={item.blocker}
                                                />
                                            ))}
                                        </div>

                                        {/* Dica contextual sobre os confirmados internamente */}
                                        {(dados.confirmados_internamente?.length || 0) > 0 && score === 100 && (
                                            <div className="audit-info-note">
                                                <FaUniversity />
                                                <span>
                                                    <strong>{dados.confirmados_internamente.length}</strong> pagamento(s)
                                                    confirmado(s) pelo sistema. Importe o extrato OFX para
                                                    verificação dupla (recomendado, não obrigatório).
                                                </span>
                                            </div>
                                        )}

                                        <div className="action-zone">
                                            <p className={`action-hint ${score === 100 ? 'ready' : ''}`}>
                                                <FaShieldAlt />
                                                {score === 100
                                                    ? 'Todas as verificações aprovadas. Período pronto para consolidação.'
                                                    : `${auditItems.filter(a => a.count > 0 && a.blocker).length} item(ns) bloqueante(s) pendente(s) — resolva para liberar.`}
                                            </p>
                                            <button
                                                className={`btn-fechar ${score === 100 ? 'ready' : 'locked'} ${closing ? 'loading' : ''}`}
                                                onClick={handleFechar}
                                                disabled={score < 100 || closing}
                                            >
                                                {closing
                                                    ? <><div className="btn-spin" /> Consolidando...</>
                                                    : score === 100
                                                        ? <><FaLock /> Consolidar e Fechar Mês <FaArrowRight /></>
                                                        : <><FaTimesCircle /> Itens Bloqueantes Pendentes</>}
                                            </button>

                                            {score < 100 && blockerCount > 0 && (
                                                <button
                                                    className="btn-ver-rotinas"
                                                    onClick={() => setActiveTab('rotinas')}
                                                >
                                                    <FaListAlt /> Ver detalhes em Rotinas
                                                    <span className="badge-inline">{blockerCount}</span>
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </main>
                )}
            </div>
        </>
    );
}